import type { Session } from "@models/ISchema";
import type {
  Assignment,
  BlockRanges,
  EditedSession,
  FormPageType,
} from "../context/formData.reducer";
import { checkedWeekIds, persistedSessionOf, sameIds, sameTime } from "./sessionComparison";

const NO_DIMENSION: Assignment = {
  blocks: false,
  day: false,
  instructors: false,
  classrooms: false,
  intervals: false,
};

const EVERY_DIMENSION: Assignment = {
  blocks: true,
  day: true,
  instructors: true,
  classrooms: true,
  intervals: true,
};

const idsOf = (items: { id?: string }[] | null | undefined): string[] =>
  (items ?? []).map(item => item?.id);

/** The time dimension: start and end time, or the number of blocks of an unscheduled session. */
const isSameTime = (pending: BlockRanges | undefined, original: BlockRanges | undefined) => {
  if (pending?.selected !== original?.selected) return false;
  if (pending?.selected === "blocks") {
    return Number(pending?.blocks ?? 0) === Number(original?.blocks ?? 0);
  }

  return (
    sameTime(pending?.startTime, original?.startTime) &&
    sameTime(pending?.endTime, original?.endTime)
  );
};

/**
 * Which dimensions of the form hold a value that differs from what the link has persisted, for one
 * session: what the "Modificado en la solicitud" pill marks. It reads the editor's pending changes,
 * since the backend's change summary is not available when a proposal is opened.
 *
 * A session the proposal creates has every dimension changed; a session without pending changes,
 * and one the proposal deletes, has none.
 */
export const getProposalChangedDimensions = (
  session: Session | EditedSession | null | undefined,
  form: FormPageType | null | undefined,
): Assignment => {
  const id = session?.id;
  if (!id) return NO_DIMENSION;

  if (Object.prototype.hasOwnProperty.call(form?.sessionsToDelete ?? {}, id)) return NO_DIMENSION;
  if ((form?.sessionsToCreate ?? []).some(created => created?.id === id)) return EVERY_DIMENSION;

  const pending = (form?.savedSessions ?? []).find(saved => saved?.id === id);
  if (pending?.isNew || pending?.isCloned) return EVERY_DIMENSION;

  const original = persistedSessionOf(pending);
  if (!pending || !original) return NO_DIMENSION;

  return {
    blocks: !isSameTime(pending.blocks, original.blocks),
    day: String(pending.blocks?.day ?? "") !== String(original.blocks?.day ?? ""),
    instructors: !sameIds(idsOf(pending.instructors), idsOf(original.instructors)),
    classrooms: !sameIds(idsOf(pending.classrooms), idsOf(original.classrooms)),
    intervals: !sameIds(checkedWeekIds(pending.intervals), checkedWeekIds(original.intervals)),
  };
};
